import React, { useState } from 'react';
import { ZoneDetails } from '../../components/Organization/ZoneDetails';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

interface OrganizationProps {
    onNavigate?: (path: string, data?: any) => void;
}

const Organization: React.FC<OrganizationProps> = ({ onNavigate }) => {
    // Company & Zones State
    const [companyName, setCompanyName] = useState('Zone');
    const [zones, setZones] = useState<string[]>(['Mumbai', 'West', 'South', 'ROM']);
    const [activeZone, setActiveZone] = useState<string>('');

    // Modals visibility state
    const [isAddZoneOpen, setIsAddZoneOpen] = useState(false);
    const [isEditCompanyOpen, setIsEditCompanyOpen] = useState(false);

    // Form input states
    const [newZoneName, setNewZoneName] = useState('');
    const [tempCompanyName, setTempCompanyName] = useState('');

    // Custom toast notification state
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [toastType, setToastType] = useState<'success' | 'info'>('success');

    const showToast = (message: string, type: 'success' | 'info' = 'success') => {
        setToastMessage(message);
        setToastType(type);
        setTimeout(() => setToastMessage(null), 3000);
    };

    // Handlers for Zone Modals (currently unused as button is commented out)
    /* const handleOpenAddZone = () => {
        setNewZoneName('');
        setIsAddZoneOpen(true);
    }; */

    const handleAddZoneSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const name = newZoneName.trim();
        if (!name) return;
        if (zones.includes(name)) {
            showToast('Zone already exists!', 'info');
            return;
        }
        setZones([...zones, name]);
        setIsAddZoneOpen(false);
        showToast(`Zone "${name}" added successfully!`);
    };

    // Handlers for Company Modals
    /* const handleOpenEditCompany = () => {
        setTempCompanyName(companyName);
        setIsEditCompanyOpen(true);
    }; */

    const handleEditCompanySubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const name = tempCompanyName.trim();
        if (!name) return;
        setCompanyName(name);
        setIsEditCompanyOpen(false);
        showToast('Company name updated successfully!');
    };

    // Rename Zone callback passed to ZoneDetails
    const handleRenameZone = (oldName: string, newName: string) => {
        setZones(zones.map(z => z === oldName ? newName : z));
        setActiveZone(newName);
    };

    const handleExportCSV = () => {
        const headers = ['Zone Name'];
        const rows = zones.map((z) => [z]);
        exportToCSV('organization_zones', headers, rows);
    };

    return (
        <div className="space-y-6">
            {/* Custom Toast Notification */}
            {toastMessage && (
                <div
                    className={`fixed top-5 right-5 z-[9999] px-4 py-3 rounded-2xl shadow-xl font-bold text-xs flex items-center gap-2.5 transition-all transform animate-bounce ${toastType === 'success'
                        ? 'bg-emerald-600 text-white shadow-emerald-600/20'
                        : 'bg-[#5D4037] text-white shadow-[#5D4037]/20'
                        }`}
                >
                    {toastType === 'success' ? (
                        <svg className="w-4 h-4 text-emerald-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    ) : (
                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
                    )}
                    {toastMessage}
                </div>
            )}

            {/* Header Actions row matching mockup */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                {/* Title and Back Button */}
                <div className="flex items-center gap-3">
                    <h3 className="text-xl font-bold go_erp-text tracking-wide m-0">
                        Organisation
                    </h3>
                </div>

                {/* Buttons Row matching styling */}
               <div className="flex flex-wrap items-center gap-3">
                    {/*  <button
                        onClick={handleOpenAddZone}
                        className="go_erp-bg text-white text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
                    >
                        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none">
                            <path d="M7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13412 13.9999 5.18208e-06 10.8659 0 7C3.22359e-06 3.13409 3.13412 0.000129983 7 0ZM7 0.976562C3.67356 0.976692 0.976566 3.67353 0.976562 7C0.976568 10.3265 3.67356 13.0233 7 13.0234C10.3265 13.0234 13.0234 10.3265 13.0234 7C13.0234 3.67345 10.3265 0.976562 7 0.976562ZM7 4.55859C7.26972 4.55859 7.48828 4.77715 7.48828 5.04688V6.51172H8.9541C9.22364 6.51194 9.44238 6.73041 9.44238 7C9.44236 7.26957 9.22362 7.48806 8.9541 7.48828H7.48828V8.95312C7.48828 9.22285 7.26972 9.44141 7 9.44141C6.73038 9.44129 6.51172 9.22278 6.51172 8.95312V7.48828H5.04688C4.77717 7.48828 4.55861 7.2697 4.55859 7C4.55859 6.73028 4.77715 6.51172 5.04688 6.51172H6.51172V5.04688C6.51172 4.77722 6.73038 4.55871 7 4.55859Z" fill="white" />
                        </svg>
                        Add Zone
                    </button>

                    <button
                        onClick={handleOpenEditCompany}
                        className="bg-[#5D4037] text-white text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 cursor-pointer hover:bg-[#4E342E] transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className='w-4 h-4' viewBox="0 0 14 14" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M9.35157 10.3404C9.70292 10.3405 9.98807 10.6247 9.98829 10.9762C9.98829 11.3277 9.70306 11.6128 9.35157 11.6129H3.01368C2.6621 11.6129 2.37696 11.3277 2.37696 10.9762C2.37718 10.6247 2.66223 10.3404 3.01368 10.3404H9.35157ZM11.0908 6.99764C11.0908 6.64598 11.376 6.36092 11.7275 6.36092C12.079 6.36111 12.3643 6.6461 12.3643 6.99764V10.1793L12.3594 10.3756C12.2614 12.3897 10.5998 13.9944 8.56056 13.9996L8.5586 13.2369V13.3639L8.56056 13.9996H3.80372L3.80567 13.3639V13.0035L3.80372 13.9996C1.69846 13.9945 -0.00431738 12.2845 8.22306e-06 10.1793V5.40584C-0.0037622 3.30106 1.69881 1.59158 3.80372 1.58651H6.18165C6.53322 1.58651 6.81837 1.87164 6.81837 2.22323C6.81812 2.57461 6.53307 2.85897 6.18165 2.85897H3.80665C2.40439 2.86242 1.27044 4.00262 1.27345 5.40487V10.1822L0.636727 10.1803H0.424813L1.27345 10.1822C1.27069 11.5843 2.40454 12.7237 3.80665 12.7271H8.55665C9.95881 12.7237 11.0935 11.5843 11.0908 10.1822L11.9395 10.1803H11.7275L11.0908 10.1822V6.99764ZM9.53419 0.519125C9.93723 0.0123213 10.6353 -0.141598 11.209 0.138266L11.3242 0.201743L11.3887 0.246664L12.7178 1.30526L12.7578 1.33944C13.0144 1.58109 13.159 1.9192 13.1572 2.27206C13.1555 2.59966 13.0258 2.91154 12.7998 3.14608L11.794 4.3492V4.35018L8.35645 8.44881L8.34864 8.45955C8.20161 8.62771 8.0052 8.74559 7.78712 8.79745L7.78028 8.7994L5.99805 9.2037C5.96252 9.21173 5.92604 9.2165 5.88966 9.21834C5.32446 9.2465 4.83186 8.83529 4.7588 8.27401C4.75529 8.24694 4.75294 8.21927 4.75294 8.19198V6.35995C4.75296 6.34573 4.75392 6.33116 4.75489 6.31698C4.77072 6.08617 4.86314 5.86654 5.01856 5.69491L8.38673 1.88729L9.53321 0.517172L9.53419 0.519125ZM6.02638 6.47811V7.8912L7.4297 7.57284L10.0547 4.44198C9.76046 4.32146 9.50152 4.14953 9.28321 3.95956C9.08099 3.78359 8.90605 3.58663 8.75782 3.38827L6.02638 6.47811ZM10.6455 1.28182C10.6041 1.2647 10.5552 1.27566 10.5274 1.31209C10.5216 1.31965 10.5159 1.32727 10.5098 1.33456L9.62306 2.39217C9.74704 2.59731 9.91397 2.82106 10.1191 2.99959C10.3799 3.22636 10.6618 3.35355 10.9775 3.33944L11.8272 2.32381C11.844 2.30368 11.8627 2.28507 11.8818 2.26717L10.6455 1.28182Z" fill="white" />
                        </svg>
                        Edit Company
                    </button> */}

                    <CSVButton onClick={handleExportCSV} />
                </div>
            </div>

            {/* Main Organisation Card containing Company name & Zone pills */}
            <div className="bg-white rounded-3xl shadow-sm border border-[#ECECF2] px-4 py-3">
                {/* Company Header */}
                <h4 className="text-base font-bold text-[#1F1F1F] tracking-wide">
                    {companyName}
                </h4>

                {/* Separator Line */}
                <hr className="border-t border-[#828282] mt-1" />

                {/* Zone Pills grid - exactly aligned with mockup */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                    {zones.map((zone) => {
                        const isActive = activeZone === zone;
                        return (
                            <button
                                key={zone}
                                onClick={() => setActiveZone(zone)}
                                className={`flex items-center justify-between px-4 py-2.5 font-medium rounded-xl text-xs cursor-pointer transition-all duration-150 ${isActive
                                    ? 'go_erp-bg text-white'
                                    : 'bg-[#F4E6E2]  text-[#5D4037]'
                                    }`}
                            >
                                <span>{zone}</span>
                                {/* Target SVG icon */}
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M8 0C12.4183 0 16 3.58173 16 8C16 12.4183 12.4183 16 8 16C3.58184 15.9999 5.81936e-06 12.4182 0 8C3.68411e-06 3.58181 3.58184 0.000129806 8 0ZM8 1.11621C4.19834 1.11634 1.11621 4.19831 1.11621 8C1.11622 11.8017 4.19834 14.8837 8 14.8838C11.8018 14.8838 14.8838 11.8018 14.8838 8C14.8838 4.19823 11.8018 1.11621 8 1.11621ZM8 5.20898C8.30825 5.20898 8.55859 5.45933 8.55859 5.76758V7.44141H10.2324C10.5407 7.44141 10.791 7.69175 10.791 8C10.791 8.30823 10.5407 8.55762 10.2324 8.55762H8.55859V10.2324C8.55859 10.5407 8.30825 10.791 8 10.791C7.69186 10.7909 7.44238 10.5406 7.44238 10.2324V8.55762H5.76758C5.45939 8.55756 5.20998 8.3082 5.20996 8C5.20996 7.69178 5.45937 7.44146 5.76758 7.44141H7.44238V5.76758C7.44238 5.45941 7.69186 5.20911 8 5.20898Z" fill={`${isActive ? 'white' : '#5D4037'}`} />
                                </svg>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Sub-dashboard for Active Zone Details (mockup matching layout) */}
            <ZoneDetails
                activeZone={activeZone}
                zones={zones}
                onRenameZone={handleRenameZone}
                onNavigate={onNavigate}
            />

            {/* MODAL: Add Zone */}
            {isAddZoneOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl border border-slate-100 space-y-4">
                        <div className="flex items-center justify-between">
                            <h5 className="text-sm font-bold text-slate-800">Add New Zone</h5>
                            <button
                                onClick={() => setIsAddZoneOpen(false)}
                                className="text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleAddZoneSubmit} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    Zone Name
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. East, North, Zone A"
                                    value={newZoneName}
                                    onChange={(e) => setNewZoneName(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-[#C62828]/20 focus:go_erp-border outline-none"
                                />
                            </div>

                            <div className="flex justify-end gap-2.5 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsAddZoneOpen(false)}
                                    className="px-4 py-2 border border-slate-200 text-slate-600 font-bold text-xs rounded-lg hover:bg-slate-50 cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 go_erp-bg text-white font-bold text-xs rounded-lg cursor-pointer"
                                >
                                    Add Zone
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: Edit Company Name */}
            {isEditCompanyOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl border border-slate-100 space-y-4">
                        <div className="flex items-center justify-between">
                            <h5 className="text-sm font-bold text-slate-800">Edit Company Name</h5>
                            <button
                                onClick={() => setIsEditCompanyOpen(false)}
                                className="text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleEditCompanySubmit} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    Company Name
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Gowardhan Milk"
                                    value={tempCompanyName}
                                    onChange={(e) => setTempCompanyName(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-[#5D4037]/20 focus:border-[#5D4037] outline-none"
                                />
                            </div>

                            <div className="flex justify-end gap-2.5 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsEditCompanyOpen(false)}
                                    className="px-4 py-2 border border-slate-200 text-slate-600 font-bold text-xs rounded-lg hover:bg-slate-50 cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-[#5D4037] hover:bg-[#4E342E] text-white font-bold text-xs rounded-lg cursor-pointer"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Organization;